import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { CancellationReasonStore } from '@/stores/CancellationReasonStore';
import { orderByField } from '@/utils/orderBy';
import type { ICancellationReasonInfo } from '~/api/ICancellationReasonInfo';
import type { ICancellationReasonRespMsg } from '~/api/messages/ICancellationReasonRespMsg';
import type { ICancellationReasonUpdateRespMsg } from '~/api/messages/ICancellationReasonUpdateRespMsg';

@SignalrListener
@service(CancellationReasonManager)
export class CancellationReasonManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(CancellationReasonStore) protected cancellationReasonStore: CancellationReasonStore;

  find(id: string) {
    return this.cancellationReasonStore.find(id);
  }

  get all() {
    const reasons = this.cancellationReasonStore.all()
      .filter((reason) => reason.status !== STATUS.DISABLED);
    return orderByField(reasons, this.cancellationReasonStore.valueKey);
  }

  reset() {
    this.cancellationReasonStore.set([]);
  }

  updateCancellationReasons(reasons: ICancellationReasonInfo[]) {
    this.cancellationReasonStore.batchUpdate(reasons);
  }

  @SignalrMessage('CancellationReasonRespMsg')
  protected onCancellationReasonRespMsg(response: ICancellationReasonRespMsg) {
    this.updateCancellationReasons(response.cancellationReasons);
  }

  @SignalrMessage('CancellationReasonUpdateRespMsg')
  protected onCancellationReasonUpdateRespMsg(response: ICancellationReasonUpdateRespMsg) {
    this.updateCancellationReasons([response.cancellationReason]);
  }
}
